import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { IMAGES } from "../data/site";

const TARGET = new Date("2026-12-13T00:30:00Z").getTime(); // Dec 12, 2026 7:30 PM Haiti (UTC-5)

export const ShareCountdown = () => {
    const { t } = useLang();
    const [days, setDays] = useState(() => Math.max(0, Math.ceil((TARGET - Date.now()) / 86400000)));
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const id = setInterval(() => setDays(Math.max(0, Math.ceil((TARGET - Date.now()) / 86400000))), 60000);
        return () => clearInterval(id);
    }, []);

    const url = window.location.origin;
    const text = `Only ${days} days until LAKOU — THE EXPERIENCE in Léogâne, Haiti (Dec 12, 2026). Afro House, Amapiano and Haitian soul under the string lights. Get your tickets:`;
    const full = `${text} ${url}`;

    const nativeShare = async () => {
        if (navigator.share) {
            try { await navigator.share({ title: "LAKOU — THE EXPERIENCE", text, url }); } catch {}
        } else {
            copyLink();
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(full);
            setCopied(true);
            toast.success("Countdown copied — paste it anywhere.");
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error("Copy failed — long-press the link instead.");
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* The shareable card */}
            <motion.div
                initial={{ opacity: 0, y: 30, rotate: -1.5 }}
                whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="relative rounded-[2rem] overflow-hidden border border-lakou-gold/30 shadow-2xl"
                data-testid="share-card"
            >
                <img src={IMAGES.hero} alt="LAKOU crowd under string lights" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-lakou-void/95 via-lakou-void/55 to-lakou-void/30" />
                <div className="relative p-8 sm:p-10 flex flex-col min-h-[320px] justify-between">
                    <div className="flex items-center justify-between">
                        <span className="font-display font-black tracking-[0.22em] text-goldgrad text-xl">LAKOU</span>
                        <span className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-lakou-offwhite/80 border border-lakou-gold/40 rounded-full px-3 py-1">Dec 12 · Léogâne</span>
                    </div>
                    <div className="text-center py-6">
                        <div className="font-display font-black text-7xl sm:text-8xl text-lakou-cream leading-none tabular-nums" data-testid="share-card-days">{days}</div>
                        <div className="mt-2 font-mono2 text-[11px] uppercase tracking-[0.35em] text-lakou-gold">{t("days_until")}</div>
                    </div>
                    <p className="text-center font-display font-bold text-sm text-lakou-offwhite/90">Born in Haiti. Felt everywhere.</p>
                </div>
            </motion.div>

            {/* Copy + share actions */}
            <div>
                <p className="font-mono2 text-xs sm:text-sm uppercase tracking-[0.25em] text-lakou-gold font-semibold mb-5">{t("spread")}</p>
                <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-lakou-cream leading-[1.05]" data-testid="share-title">
                    {t("tell_people", { days })}
                </h2>
                <p className="mt-5 text-lakou-grey leading-relaxed max-w-md">
                    Movements grow by word of mouth. Share the countdown and put your circle on notice — Léogâne is only the beginning.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                    <button onClick={nativeShare} data-testid="share-native"
                        className="inline-flex items-center gap-2 rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] px-6 py-3.5 transition-colors duration-300">
                        <Share2 size={14} /> Share
                    </button>
                    <a href={`https://wa.me/?text=${encodeURIComponent(full)}`} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp"
                        className="inline-flex items-center gap-2 rounded-full border border-lakou-gold/40 hover:border-lakou-gold text-lakou-cream hover:text-lakou-gold font-display font-bold text-xs uppercase tracking-[0.15em] px-6 py-3.5 transition-colors duration-300">
                        <MessageCircle size={14} /> WhatsApp
                    </a>
                    <button onClick={copyLink} data-testid="share-copy"
                        className="inline-flex items-center gap-2 rounded-full border border-lakou-gold/40 hover:border-lakou-gold text-lakou-cream hover:text-lakou-gold font-display font-bold text-xs uppercase tracking-[0.15em] px-6 py-3.5 transition-colors duration-300">
                        {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? t("share_copied") : t("share_copy")}
                    </button>
                </div>
            </div>
        </div>
    );
};
